import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  CreateAccountBody,
  CreateAccountResult,
  RegistrationApiError,
  StartLinkBody,
  registrationApi
} from './registration.api';

export const registrationKeys = {
  checkout: (id: string) => ['registration', 'checkout', id] as const,
  geo: ['registration', 'geo'] as const,
  gateway: (m: string) => ['registration', 'gateway', m] as const
};

/**
 * The session is fetched once per page life: returning from a payment gateway
 * (focus, reconnect, remount) must not re-validate a session that may by then
 * be consumed.
 */
export function useCheckoutSession(id: string | null) {
  return useQuery({
    queryKey: registrationKeys.checkout(id ?? ''),
    queryFn: () => registrationApi.validateCheckout(id as string),
    enabled: !!id,
    staleTime: Infinity,
    gcTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    // Retry only when the server never answered (network failure).
    retry: (n: number, e: RegistrationApiError) =>
      !(e instanceof RegistrationApiError && e.status > 0) && n < 1
  });
}

export function useGeo(enabled: boolean) {
  return useQuery({
    queryKey: registrationKeys.geo,
    queryFn: () => registrationApi.geo(),
    enabled,
    staleTime: Infinity
  });
}

export function useResolveGateway(paymentMethod: string | null) {
  return useQuery({
    queryKey: registrationKeys.gateway(paymentMethod ?? ''),
    queryFn: () => registrationApi.resolveGateway(paymentMethod as string),
    enabled: !!paymentMethod,
    staleTime: 5 * 60 * 1000
  });
}

/**
 * Reads the gateway for a brand on demand (a pick), sharing useResolveGateway's cache entry.
 * A read, not a side effect, so it goes through the query cache rather than a mutation.
 */
export function useFetchGateway() {
  const queryClient = useQueryClient();
  return useCallback(
    (paymentMethod: string) =>
      queryClient.fetchQuery({
        queryKey: registrationKeys.gateway(paymentMethod),
        queryFn: () => registrationApi.resolveGateway(paymentMethod),
        staleTime: 5 * 60 * 1000
      }),
    [queryClient]
  );
}

export function useStartFaceScan() {
  return useMutation<
    { verificationUrl: string; bizToken: string },
    RegistrationApiError,
    void
  >({ mutationFn: () => registrationApi.startFaceScan() });
}

type SendOtpVars =
  | {
      kind: 'phone';
      phoneNumber: string;
      countryCode: string;
      channel: 'sms' | 'whatsapp';
    }
  | { kind: 'email'; email: string };

export function useSendOtp() {
  return useMutation<void, RegistrationApiError, SendOtpVars>({
    mutationFn: (v) =>
      v.kind === 'phone'
        ? registrationApi.sendPhoneOtp({
            phoneNumber: v.phoneNumber,
            countryCode: v.countryCode,
            channel: v.channel
          })
        : registrationApi.sendEmailOtp({ email: v.email })
  });
}

type VerifyOtpVars =
  | { kind: 'phone'; phoneNumber: string; countryCode: string; otp: string }
  | { kind: 'email'; email: string; otp: string };

export function useVerifyOtp() {
  return useMutation<string, RegistrationApiError, VerifyOtpVars>({
    mutationFn: (v) =>
      v.kind === 'phone'
        ? registrationApi.verifyPhoneOtp({
            phoneNumber: v.phoneNumber,
            countryCode: v.countryCode,
            otp: v.otp
          })
        : registrationApi.verifyEmailOtp({ email: v.email, otp: v.otp })
  });
}

export function useCreateAccount() {
  return useMutation<
    CreateAccountResult,
    RegistrationApiError,
    CreateAccountBody
  >({ mutationFn: (b) => registrationApi.createAccount(b) });
}

export function useStartLink() {
  return useMutation<
    { payUrl?: string },
    RegistrationApiError,
    { body: StartLinkBody; accessToken: string }
  >({
    mutationFn: ({ body, accessToken }) =>
      registrationApi.startLink(body, accessToken)
  });
}
